const MIN_POOL_SIZE = 10;
const MAX_POOL_SIZE = 15;

const ICONS = {
  birth: '📅',
  nationality: '🌍',
  physical: '📏',
  role: '⚡',
  style: '🎯',
  debut: '🚀',
  team: '👕',
  international: '🌐',
  career: '⌛',
  statistics: '📊',
  scoring: '🔥',
  bowling: '🎳',
  record: '🏆',
  award: '🏅',
  title: '🥇',
  ranking: '📈',
  draft: '🎟️',
  education: '🎓',
  event: '🏟️',
  fact: '💡'
};

const FACT_WORDS = /\b(born|first|debut|represented|played|joined|signed|scored|goals?|runs?|wickets?|points?|matches|appearances?|captain|won|winner|champion|championship|title|trophy|medal|award|record|ranked|ranking|draft|selected|inducted|retired|centur(?:y|ies)|podiums?|poles?|victories|knockouts?|grand slam|olympic|world cup|league|tour)\b/i;
const SPORT_SIGNAL = /\b(cricket|test|odi|t20|football|soccer|premier league|champions league|club|basketball|nba|tennis|wimbledon|grand slam|boxing|heavyweight|fight|knockout|golf|pga|masters|olympic|swimming|formula one|grand prix|world championship|tournament|season|career|debut|goals?|runs?|wickets?|points?|appearances?|centur(?:y|ies)|titles?|troph(?:y|ies)|medals?|awards?|record|rank(?:ed|ing)?)\b/i;
const SPORT_PATTERNS = {
  Cricket: /\b(cricket|tests?|odis?|t20i?s?|innings|batt(?:er|ing|sman)|bowl(?:er|ing)|wickets?|centur(?:y|ies)|ipl)\b/i,
  Football: /\b(football|premier league|champions league|league|club|goals?|appearances?|world cup|africa cup|uefa|fifa|season|winger|striker|midfielder|defender|goalkeeper|captain)\b/i,
  Basketball: /\b(basketball|nba|wnba|points?|rebounds?|assists?|steals?|blocks?|draft|championships?)\b/i,
  Tennis: /\b(tennis|australian open|french open|us open|wimbledon|grand slam|singles|doubles|wta|atp|ranking|titles?|tournament)\b/i,
  Boxing: /\b(boxing|boxer|heavyweight|lightweight|welterweight|fight|fights|bout|knockout|ring|titles?|champion|olympic|gold medal)\b/i,
  MMA: /\b(mma|mixed martial arts|ufc|fight|bout|knockout|submission|champion|titles?)\b/i,
  Golf: /\b(golf|pga|masters|open championship|ryder cup|major|tournament|tour)\b/i,
  Athletics: /\b(athletics|sprinter|metres?|meters?|relay|olympic|world championships?|race|medal|record)\b/i,
  Swimming: /\b(swimming|swimmer|freestyle|butterfly|backstroke|breaststroke|medley|olympic|world championships?|medal|record)\b/i,
  'Formula 1': /\b(formula one|formula 1|grand prix|races?|podiums?|poles?|world championships?|constructor)\b/i,
  'Ice Hockey': /\b(ice hockey|nhl|goals?|assists?|points?|stanley cup|season)\b/i,
  Baseball: /\b(baseball|mlb|home runs?|batting|pitcher|outfielder|world series|yankees|red sox|dodgers)\b/i
};
const GENERIC_ONLY = /^(?:this player\s+)?(?:is|was)\s+(?:an?\s+)?(?:former\s+)?(?:professional\s+|international\s+)?(?:cricketer|footballer|basketball player|tennis player|boxer|golfer|athlete|swimmer|racing driver)\.?$/i;

const cleanText = (value = '') => value
  .replace(/\[[^\]]*\]/g, ' ')
  .replace(/\s+/g, ' ')
  .replace(/\s+([,;:])/g, '$1')
  .trim();

const normaliseLabel = (value = '') => cleanText(value)
  .toLowerCase()
  .replace(/[^a-z0-9/ ]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getPlayerAliases = (player) => {
  const names = [player.name, player.searchTerm?.replace(/\s*\([^)]*\)\s*$/, '')].filter(Boolean);
  const parts = player.name.trim().split(/\s+/);
  if (parts.length > 1) names.push(parts[parts.length - 1]);
  if (parts.length > 2 && /^(?:Jr\.?|Sr\.?|II|III|IV)$/i.test(parts[parts.length - 1])) {
    names.push(parts[parts.length - 2]);
  }
  if (parts.length > 2 && /^(?:de|del|van|von|da|dos)$/i.test(parts[parts.length - 2])) {
    names.push(parts.slice(-2).join(' '));
  }
  return [...new Set(names)].sort((a, b) => b.length - a.length);
};

const removePlayerName = (text, aliases) => {
  let result = text;
  aliases.forEach((alias) => {
    result = result.replace(
      new RegExp(`\\b${escapeRegex(alias)}(?:['’]s|['’])?`, 'gi'),
      (match) => /['’]/.test(match) ? "This player's" : 'This player'
    );
  });
  return cleanText(result)
    .replace(/([,;]\s+)This player\b/g, '$1the player')
    .replace(/^(?:This player|He|She) (?:has |had )?made\b/i, 'Made')
    .replace(/^(?:This player|He|She) (?:has |had )?scored\b/i, 'Scored')
    .replace(/^(?:This player|He|She) (?:has |had )?won\b/i, 'Won')
    .replace(/^(?:This player|He|She) (?:has |had )?played\b/i, 'Played')
    .replace(/^(?:This player|He|She) (?:has |had )?represented\b/i, 'Represented')
    .replace(/^(?:This player|He|She) (?:was|is) born\b/i, 'Born')
    .replace(/^(?:This player|He|She) (?:was|is) named\b/i, 'Was named')
    .replace(/^(?:This player|He|She) holds?\b/i, 'Holds')
    .replace(/^At international level, (?:This player|he|she) represented\b/i, 'Represented')
    .replace(/\bwinning This player\b/g, 'earning this player')
    .replace(/\bbringing This player\b/g, 'bringing this player')
    .replace(/^(?:His|Her)\b/i, "This player's");
};

const parseInfoboxRows = (html) => {
  if (!html || typeof DOMParser === 'undefined') return [];
  const documentNode = new DOMParser().parseFromString(html, 'text/html');
  const rows = [];
  let pendingHeader = '';

  const cellText = (cell) => {
    const clone = cell.cloneNode(true);
    clone.querySelectorAll('style, script, sup.reference, .mw-editsection, [style*="display:none"]').forEach((node) => node.remove());
    clone.querySelectorAll('br').forEach((node) => node.replaceWith(' '));
    return cleanText(clone.textContent);
  };

  const pushRow = (label, values) => {
    const cleanedLabel = cleanText(label).replace(/:$/, '');
    const cleanedValues = values.map(cleanText).filter(Boolean);
    if (!cleanedLabel || !cleanedValues.length || cleanedLabel.length > 55) return;
    rows.push({ label: cleanedLabel, key: normaliseLabel(cleanedLabel), values: cleanedValues });
  };

  documentNode.querySelectorAll('table.infobox tr').forEach((row) => {
    const cells = Array.from(row.children).filter((cell) => /^(TH|TD)$/.test(cell.tagName));
    if (!cells.length) return;

    if (cells.length === 1) {
      const cell = cells[0];
      if (cell.tagName === 'TH' && cell.classList.contains('infobox-header')) {
        pendingHeader = cellText(cell);
        return;
      }

      if (cell.tagName === 'TD' && cell.classList.contains('infobox-full-data')) {
        const labelledBlocks = Array.from(cell.querySelectorAll('div')).filter((block) => block.querySelector(':scope > b'));
        if (labelledBlocks.length) {
          labelledBlocks.forEach((block) => {
            const labelNode = block.querySelector('b');
            const clone = block.cloneNode(true);
            clone.querySelector('b')?.remove();
            pushRow(labelNode.textContent, [cellText(clone)]);
          });
        } else {
          const labelNode = cell.querySelector(':scope > b');
          if (labelNode && /:$/.test(labelNode.textContent.trim())) {
            const clone = cell.cloneNode(true);
            clone.querySelector(':scope > b')?.remove();
            pushRow(labelNode.textContent, [cellText(clone)]);
          } else if (pendingHeader && !/^(?:teams|career highlights and awards|statistics)$/i.test(pendingHeader)) {
            pushRow(pendingHeader, [cellText(cell)]);
          }
        }
        pendingHeader = '';
      }
      return;
    }

    pushRow(cellText(cells[0]), cells.slice(1).map(cellText));
    pendingHeader = '';
  });

  return rows;
};

export const parseWikipediaInfoboxRows = parseInfoboxRows;

const getArticleSentences = (articleText) => cleanText(
  articleText
    .replace(/^=+.*?=+$/gm, ' ')
    .replace(/\n+/g, ' ')
)
  .replace(/(\d)\.(\d)/g, '$1∯$2')
  .replace(/\bNo\./g, 'No∯')
  .match(/[^.!?]+[.!?]+|[^.!?]+$/g)
  ?.map((sentence) => sentence.replace(/∯/g, '.')) || [];

const classifySentence = (text) => {
  const lower = text.toLowerCase();
  if (/\b(born|birth)\b/.test(lower)) return { category: 'birth', difficulty: 1 };
  if (/\b(height|tall|weighs?)\b/.test(lower)) return { category: 'physical', difficulty: 2 };
  if (/\b(national team|national side|represented .* internationally|international career)\b/.test(lower)) return { category: 'international', difficulty: 2 };
  if (/\b(plays? as|batter|batsman|bowler|all-rounder|wicket-keeper|goalkeeper|defender|midfielder|forward|striker|guard|center|stance|right-handed|left-handed)\b/.test(lower)) return { category: 'role', difficulty: 3 };
  if (/\b(record|fastest|first player|only player|youngest(?:-ever)? (?:player|driver|champion)|oldest(?:-ever)? (?:player|driver|champion)|highest (?:score|goals?|runs?|points?|ranking))\b/.test(lower)) {
    const highlyDistinctive = /\b(?:world record|first player|only player|all-time|fastest|youngest-ever|oldest-ever|broke .* record|set .* record|record for most)\b/.test(lower);
    return { category: 'record', difficulty: highlyDistinctive ? 5 : 4 };
  }
  if (/\b(award|named .* of the year|hall of fame|mvp|ballon d'or)\b/.test(lower)) return { category: 'award', difficulty: 5 };
  if (/\b(won|winner|champion|championships?|titles?|troph(?:y|ies)|medals?|world cup|grand slam|olympic)\b/.test(lower)) return { category: 'title', difficulty: 5 };
  if (/\b(debut|first appeared|first match|turned professional)\b/.test(lower)) return { category: 'debut', difficulty: 3 };
  if (/\b(joined|signed|played for|club|team|franchise)\b/.test(lower)) return { category: 'team', difficulty: 3 };
  if (/\b(rank|ranked|ranking)\b/.test(lower)) return { category: 'ranking', difficulty: 4 };
  if (/\d/.test(lower) && /\b(goals?|runs?|wickets?|points?|matches|appearances?|centur(?:y|ies)|knockouts?|podiums?|poles?)\b/.test(lower)) return { category: 'statistics', difficulty: 4 };
  return { category: 'fact', difficulty: 3 };
};

const isPlayerSubject = (sentence, aliases) => {
  const escapedAliases = aliases.map(escapeRegex).join('|');
  const directSubject = new RegExp(`^(?:${escapedAliases}|he|she|his|her)\\b`, 'i');
  const datedSubject = new RegExp(`^(?:in|on|during|after|before|by|at|with|while|although|despite|following)\\b.{0,85}\\b(?:${escapedAliases}|he|she)\\b`, 'i');
  return directSubject.test(sentence.trim()) || datedSubject.test(sentence.trim());
};

const shortenVerifiedSentence = (sentence, aliases, sport) => {
  if (/^(?:His|Her)\s+(?:brother|sister|niece|nephew|father|mother|son|daughter|family)\b/i.test(sentence.trim())) return null;
  if (aliases.some((alias) => new RegExp(`^${escapeRegex(alias)}(?:['’]s|['’])\\s+(?:brother|sister|niece|nephew|father|mother|son|daughter|family)\\b`, 'i').test(sentence.trim()))) return null;
  let text = removePlayerName(cleanText(sentence), aliases);
  if (!text || GENERIC_ONLY.test(text) || !FACT_WORDS.test(text)) return null;
  if (!SPORT_SIGNAL.test(text)) return null;
  if (SPORT_PATTERNS[sport] && !SPORT_PATTERNS[sport].test(text)) return null;
  if (sport === 'Baseball' && /\b(?:broad[- ]jump|rushing yards?|football record|basketball record)\b/i.test(text)) return null;
  if (/\b(?:citation needed|according to|rumou?red|reportedly|claimed|alleged)\b/i.test(text)) return null;
  if (/\b(?:sisters|brothers|siblings)\b/i.test(text)) return null;
  if (/\b(?:missed|missing|eliminated|withdrew|injur(?:y|ed|ing)|sciatica|in tears|suspension|medical)\b/i.test(text)) return null;
  if (/\b(?:widely regarded|greatest|best player|top players?|richest|earnings|net worth|emotional|debts?|casino|entertainment|entertain fans|extremely skilled|knockout danger)\b/i.test(text)) return null;
  if (/\b(?:wished|believing|skeptical|hoped|felt that)\b/i.test(text)) return null;
  if (/["“”]/.test(text)) return null;

  if (text.length > 125) {
    const firstClause = text.split(/;\s+|,\s+(?:while|whereas|which|who|making|matching|setting|becoming|before|after|and)\s+/i)[0];
    if (firstClause.length >= 35 && firstClause.length <= 135 && FACT_WORDS.test(firstClause)) {
      text = firstClause;
    }
  }

  if (text.length < 18 || text.length > 135) return null;
  if (/\b(?:No|vs|including|and|or|of|the)$/i.test(text)) return null;
  if ((text.match(/\(/g) || []).length !== (text.match(/\)/g) || []).length) return null;
  return text.replace(/[.]+$/, '');
};

const formatValue = (values) => cleanText(values.join(' · '))
  .replace(/([a-z])(?=(?:right|left)[ -])/gi, '$1 / ')
  .slice(0, 130);

const addStructuredClues = (rows, player, add) => {
  let competitions = [];

  rows.forEach(({ key, values }) => {
    const value = formatValue(values);
    if (!value) return;

    if (key === 'competition') {
      competitions = values.map(cleanText);
      return;
    }

    if (/^(?:born|date of birth)$/.test(key)) {
      const year = value.match(/\b(18|19|20)\d{2}\b/)?.[0];
      if (year) add(`Born in ${year}`, 'birth', 1, 'Wikipedia infobox');
      return;
    }

    if (/^(?:height|listed height)$/.test(key)) {
      add(`Listed at ${value} tall`, 'physical', 2, 'Wikipedia infobox');
      return;
    }

    if (key === 'nationality') {
      const nationality = cleanText(value.replace(/\([^)]*\)/g, ''));
      if (nationality && !/^(?:professional|former|retired|international)$/i.test(nationality)) {
        add(`Nationality: ${nationality}`, 'nationality', 2, 'Wikipedia infobox');
      }
      return;
    }

    if (/^(?:national side|country|representing)$/.test(key)) {
      const country = cleanText(value.replace(/\([^)]*\)/g, ''));
      if (country && !/^(?:professional|former|retired|international)$/i.test(country)) {
        add(`Represented ${country} internationally`, 'international', 2, 'Wikipedia infobox');
      }
      const span = value.match(/\b((?:18|19|20)\d{2})\s*[–-]\s*((?:19|20)\d{2})\b/);
      if (span) add(`International career spanned ${span[1]}–${span[2]}`, 'career', 3, 'Wikipedia infobox');
      return;
    }

    if (/^(?:role|position|position s|playing position)$/.test(key)) {
      add(`Played primarily as ${value}`, 'role', 3, 'Wikipedia infobox');
      return;
    }

    if (/^(?:event|events|event s)$/.test(key)) {
      add(`Competed in ${value}`, 'role', 3, 'Wikipedia infobox');
      return;
    }

    if (key === 'batting') {
      add(`Batted ${value.toLowerCase()}`, 'style', 2, 'Wikipedia infobox');
      return;
    }

    if (key === 'bowling') {
      add(`Bowled ${value.toLowerCase()}`, 'bowling', 3, 'Wikipedia infobox');
      return;
    }

    if (/^(?:plays|handedness|stance)$/.test(key)) {
      add(`Playing style: ${value}`, 'style', 2, 'Wikipedia infobox');
      return;
    }

    if (/^(?:batted|throws|threw)$/.test(key)) {
      add(`${key === 'batted' ? 'Batted' : 'Threw'} ${value.toLowerCase()}`, 'style', 2, 'Wikipedia infobox');
      return;
    }

    if (/\bdebut\b/.test(key)) {
      const format = key.replace(/\s*\([^)]*\)/g, '').replace(/\bdebut\b.*/i, 'debut').trim();
      const displayFormat = (format || 'a senior debut')
        .replace(/^test\b/i, 'Test')
        .replace(/^odi\b/i, 'ODI')
        .replace(/^t20i\b/i, 'T20I')
        .replace(/^mlb\b/i, 'MLB')
        .replace(/^nba\b/i, 'NBA');
      add(`Made ${displayFormat}: ${value}`, 'debut', 3, 'Wikipedia infobox');
      return;
    }

    if (/^(?:playing career|professional career|years active|turnedpro|turned pro)$/.test(key)) {
      add(`Professional career: ${value}`, 'career', 2, 'Wikipedia infobox');
      return;
    }

    if (/^(?:college|college s|university|high school)$/.test(key)) {
      add(`Developed at ${value}`, 'education', 2, 'Wikipedia infobox');
      return;
    }

    if (/^(?:nba draft|drafted|draft)$/.test(key)) {
      add(`Draft detail: ${value}`, 'draft', 4, 'Wikipedia infobox');
      return;
    }

    if (/^(?:current team|team|club|former teams)$/.test(key) && value.length <= 95) {
      add(`Team: ${value}`, 'team', 3, 'Wikipedia infobox');
      return;
    }

    if (/^(?:highest ranking|career high singles ranking|career high doubles ranking)$/.test(key)) {
      add(`Reached a career-high ranking of ${value}`, 'ranking', 4, 'Wikipedia infobox');
      return;
    }

    if (/^(?:career titles|singles titles|tour wins|professional wins|pga tour wins)$/.test(key)) {
      add(`Career titles/wins: ${value}`, 'title', 5, 'Wikipedia infobox');
      return;
    }

    if (/^(?:total fights|wins|wins by ko|losses|draws)$/.test(key) && player.sport === 'Boxing') {
      add(`${key.replace(/\b\w/g, (letter) => letter.toUpperCase())}: ${value}`, key === 'wins by ko' ? 'record' : 'statistics', 4, 'Wikipedia infobox');
      return;
    }

    if (/^(?:championships|wins|podiums|career points|pole positions|fastest laps|entries)$/.test(key) && player.sport === 'Formula 1') {
      add(`${key.replace(/\b\w/g, (letter) => letter.toUpperCase())}: ${value}`, key === 'championships' ? 'title' : 'statistics', key === 'championships' ? 5 : 4, 'Wikipedia infobox');
      return;
    }

    if (/^(?:batting average|hits|home runs|runs batted in|slugging percentage|win loss record|earned run average|strikeouts)$/.test(key) && player.sport === 'Baseball') {
      const labels = {
        'batting average': 'Career batting average',
        hits: 'Career hits',
        'home runs': 'Career home runs',
        'runs batted in': 'Career RBIs',
        'slugging percentage': 'Career slugging percentage',
        'win loss record': 'Pitching win–loss record',
        'earned run average': 'Career earned run average',
        strikeouts: 'Career strikeouts'
      };
      add(`${labels[key]}: ${value}`, 'statistics', 4, 'Wikipedia infobox');
      return;
    }

    if (competitions.length && /^(?:matches|runs scored|wickets|top score|100s\/50s|goals|appearances|points)$/.test(key)) {
      values.slice(0, competitions.length).forEach((stat, index) => {
        const competition = competitions[index];
        if (!competition || !stat || stat === '–' || stat === '-') return;
        if (key === 'matches') add(`Played ${stat} ${competition} matches`, 'statistics', 3, 'Wikipedia infobox');
        if (key === 'runs scored') add(`Scored ${stat} runs in ${competition}`, 'scoring', 4, 'Wikipedia infobox');
        if (key === 'wickets') add(`Took ${stat} wickets in ${competition}`, 'bowling', 4, 'Wikipedia infobox');
        if (key === 'top score') add(`Top ${competition} score: ${stat}`, 'statistics', 4, 'Wikipedia infobox');
        if (key === '100s/50s') add(`Recorded ${stat} centuries/fifties in ${competition}`, 'scoring', 4, 'Wikipedia infobox');
        if (key === 'goals') add(`Scored ${stat} goals in ${competition}`, 'scoring', 4, 'Wikipedia infobox');
        if (key === 'appearances') add(`Made ${stat} appearances in ${competition}`, 'statistics', 4, 'Wikipedia infobox');
        if (key === 'points') add(`Scored ${stat} points in ${competition}`, 'scoring', 4, 'Wikipedia infobox');
      });
    }
  });
};

const addLeadNationality = (articleText, add) => {
  const lead = cleanText(articleText).slice(0, 900);
  const profession = '(?:cricketer|footballer|basketball player|tennis player|boxer|golfer|athlete|swimmer|racing driver|baseball player|ice hockey player|mixed martial artist)';
  const nationalityPattern = new RegExp(`\\b(?:is|was)\\s+(?:an?|the)\\s+([A-Z][A-Za-z-]+(?:\\s+[A-Z][A-Za-z-]+){0,2}?)\\s+(?:(?:former|retired)\\s+)?(?:(?:professional|international)\\s+)*${profession}\\b`);
  const nationality = lead.match(nationalityPattern)?.[1];
  if (nationality && !/\b(?:professional|former|retired|international)\b/i.test(nationality)) {
    add(`Nationality: ${nationality}`, 'nationality', 2, 'Wikipedia article', lead.match(nationalityPattern)[0]);
  }
};

const createBalancedPool = (candidates) => {
  const pool = [];
  const used = new Set();
  const addCandidate = (candidate) => {
    const signature = candidate.text.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (used.has(signature)) return;
    used.add(signature);
    pool.push(candidate);
  };

  const categoryPriority = {
    birth: 10,
    physical: 8,
    nationality: 10,
    international: 9,
    role: 8,
    style: 7,
    debut: 10,
    team: 9,
    career: 8,
    education: 7,
    draft: 9,
    scoring: 9,
    statistics: 9,
    ranking: 10,
    record: 12,
    award: 11,
    title: 10,
    event: 7,
    fact: 5
  };

  for (let difficulty = 1; difficulty <= 5; difficulty += 1) {
    candidates
      .filter((candidate) => candidate.difficulty === difficulty)
      .sort((a, b) => difficulty === 5
        ? b.specificity - a.specificity || (categoryPriority[b.category] || 0) - (categoryPriority[a.category] || 0)
        : (categoryPriority[b.category] || 0) - (categoryPriority[a.category] || 0) || b.specificity - a.specificity)
      .slice(0, 3)
      .forEach(addCandidate);
  }

  candidates.forEach((candidate) => {
    if (pool.length < MAX_POOL_SIZE) addCandidate(candidate);
  });

  return pool.slice(0, MAX_POOL_SIZE).sort((a, b) => a.difficulty - b.difficulty);
};

export const selectGameClues = (pool) => {
  const selected = [];
  const usedCategories = new Set();
  const selectionPriority = {
    birth: 12,
    physical: 10,
    nationality: 12,
    international: 11,
    style: 9,
    debut: 12,
    role: 11,
    team: 10,
    career: 9,
    education: 8,
    bowling: 7,
    statistics: 12,
    scoring: 11,
    ranking: 10,
    draft: 9,
    fact: 7,
    record: 6,
    award: 6,
    title: 6
  };

  [1, 2, 3, 4, 5].forEach((targetDifficulty) => {
    const available = pool
      .filter((candidate) => !usedCategories.has(candidate.category) && !selected.includes(candidate))
      .sort((a, b) => {
        const aDistance = Math.abs(a.difficulty - targetDifficulty);
        const bDistance = Math.abs(b.difficulty - targetDifficulty);
        if (aDistance !== bDistance) return aDistance - bDistance;
        if (a.difficulty !== b.difficulty) return targetDifficulty === 5 ? b.difficulty - a.difficulty : a.difficulty - b.difficulty;
        return targetDifficulty === 5
          ? b.specificity - a.specificity
          : (selectionPriority[b.category] || 0) - (selectionPriority[a.category] || 0);
      });
    const choice = available[0];
    if (choice) {
      selected.push(choice);
      usedCategories.add(choice.category);
    }
  });

  if (selected.length < 5) {
    pool.forEach((candidate) => {
      if (selected.length < 5 && !selected.includes(candidate)) selected.push(candidate);
    });
  }

  return selected
    .sort((a, b) => a.difficulty - b.difficulty || a.specificity - b.specificity)
    .slice(0, 5)
    .map((clue, index) => ({ ...clue, order: index + 1 }));
};

export const buildVerifiedClues = ({ articleText, infoboxHtml, player, wikiUrl }) => {
  const aliases = getPlayerAliases(player);
  const candidates = [];
  const signatures = new Set();

  const add = (text, category, difficulty, sourceType, sourceText = text) => {
    const cleaned = cleanText(text).replace(/[.]+$/, '');
    const signature = cleaned.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!cleaned || cleaned.length < 12 || cleaned.length > 135 || signatures.has(signature)) return;
    if (GENERIC_ONLY.test(cleaned) || /Nationality:\s*(?:professional|former|retired|international)/i.test(cleaned)) return;
    if (aliases.some((alias) => new RegExp(`\\b${escapeRegex(alias)}\\b`, 'i').test(cleaned))) return;
    signatures.add(signature);
    const specificity = difficulty * 10
      + (/\b(?:only (?:player|athlete|driver|boxer|golfer)|first (?:player|athlete|driver|boxer|golfer)|all-time|world record|record for most|broke .* record|set .* record|fastest|youngest-ever|oldest-ever)\b/i.test(cleaned) ? 6 : 0)
      + (/\b(?:world cup|olympic|champions league|grand slam|hall of fame|championship)\b/i.test(cleaned) ? 3 : 0)
      + (/\d/.test(cleaned) ? 1 : 0);
    candidates.push({
      text: cleaned,
      icon: ICONS[category] || ICONS.fact,
      category,
      difficulty,
      specificity,
      verified: true,
      sourceType,
      sourceText: cleanText(sourceText),
      sourceUrl: wikiUrl
    });
  };

  addStructuredClues(parseInfoboxRows(infoboxHtml), player, add);
  addLeadNationality(articleText, add);

  getArticleSentences(articleText).forEach((sentence) => {
    const cleanedSentence = cleanText(sentence);
    if (!isPlayerSubject(cleanedSentence, aliases)) return;
    const clueText = shortenVerifiedSentence(cleanedSentence, aliases, player.sport);
    if (!clueText) return;
    const { category, difficulty } = classifySentence(clueText);
    add(clueText, category, difficulty, 'Wikipedia article', cleanedSentence);
  });

  const pool = createBalancedPool(candidates);
  if (pool.length < MIN_POOL_SIZE) {
    throw new Error(`Wikipedia supplied only ${pool.length} usable verified clues for ${player.name}`);
  }

  const clues = selectGameClues(pool);
  if (clues.length !== 5 || new Set(clues.map((clue) => clue.category)).size !== 5) {
    const availableCategories = [...new Set(pool.map((clue) => clue.category))].join(', ');
    throw new Error(`Could not create five diverse verified clues for ${player.name}; categories: ${availableCategories}`);
  }

  return { cluePool: pool, clues };
};
